import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import EventsService from "../services/EventsService";
import { getSocket } from "../socket";
import {
  ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, Cell,
} from "recharts";
import { format } from "date-fns";

import { TimelineSelector } from "../components/TimelineSelector";
import { TimepointCalendar } from "../components/TimepointCalendar";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];

const GPIO_ORDER = [5, 18, 19, 23];
function sortActionsByGPIO(actions) {
  const gpio = actions.filter(a => !String(a.typeId).startsWith("custom-"));
  const custom = actions.filter(a => String(a.typeId).startsWith("custom-"));
  gpio.sort((a, b) => {
    const ia = GPIO_ORDER.indexOf(Number(a.typeId));
    const ib = GPIO_ORDER.indexOf(Number(b.typeId));
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  custom.sort((a, b) => {
    const ia = parseInt(String(a.typeId).split("-")[1]) || 0;
    const ib = parseInt(String(b.typeId).split("-")[1]) || 0;
    return ib - ia;
  });
  return [...gpio, ...custom];
}

const exponentialBackoff = (max, delay, toTry, success, fail) => {
  toTry()
    .then((result) => success(result))
    .catch((_) => {
      if (max === 0) {
        return fail();
      }
      setTimeout(function () {
        exponentialBackoff(--max, delay * 2, toTry, success, fail);
      }, delay * 1000);
    });
};

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatInterval(ms) {
  const totalSeconds = ms / 1000;
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = (totalSeconds % 60).toFixed(2);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatElapsed(startUnix) {
  const elapsed = Math.floor(Date.now() / 1000) - startUnix;
  const m = Math.floor(Math.max(0, elapsed) / 60);
  const s = Math.max(0, elapsed) % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatCountdown(ms) {
  if (ms <= 0) return "0:00";
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Alarm sound using Web Audio API
let audioCtx = null;
function playAlarmSound() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.frequency.value = 880;
    osc.type = "square";
    gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.8);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.8);
    setTimeout(() => {
      const osc2 = audioCtx.createOscillator();
      const gain2 = audioCtx.createGain();
      osc2.connect(gain2);
      gain2.connect(audioCtx.destination);
      osc2.frequency.value = 1100;
      osc2.type = "square";
      gain2.gain.setValueAtTime(0.15, audioCtx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.6);
      osc2.start(audioCtx.currentTime);
      osc2.stop(audioCtx.currentTime + 0.6);
    }, 200);
  } catch {}
}

function AlarmModal({ show, onClose, event, alarms, onSave, onDelete }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [actionTypeId, setActionTypeId] = useState("");
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(30);
  const [isRecurring, setIsRecurring] = useState(true);
  const [isGlobal, setIsGlobal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const resetForm = () => {
    setName(""); setActionTypeId(""); setMinutes(0); setSeconds(30);
    setIsRecurring(true); setIsGlobal(false); setEditingId(null);
  };

  const handleEdit = (alarm) => {
    setEditingId(alarm.id);
    setName(alarm.name);
    setActionTypeId(alarm.actionTypeId);
    setMinutes(Math.floor(alarm.durationSeconds / 60));
    setSeconds(alarm.durationSeconds % 60);
    setIsRecurring(alarm.isRecurring);
    setIsGlobal(!alarm.eventId);
  };

  const handleSubmit = () => {
    const durationSeconds = minutes * 60 + seconds;
    if (!actionTypeId || durationSeconds < 1) return;
    onSave({
      id: editingId,
      name: name || undefined,
      actionTypeId,
      durationSeconds,
      isRecurring,
      eventId: isGlobal ? undefined : event.id,
    });
    resetForm();
  };

  if (!show) return null;

  return (
    <div className="alarm-modal-overlay" onClick={onClose}>
      <div className="alarm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="alarm-modal-header">
          <h6>{t("event.alarmModal.title")}</h6>
          <button className="alarm-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="alarm-modal-body">
          {/* Existing alarms */}
          {alarms.length > 0 && (
            <div className="alarm-list">
              {alarms.map((alarm) => {
                const action = event.actions.find(
                  (a) => String(a.typeId) === String(alarm.actionTypeId)
                );
                const colorIdx = event.actions.findIndex(
                  (a) => String(a.typeId) === String(alarm.actionTypeId)
                );
                return (
                  <div key={alarm.id} className="alarm-list-item">
                    <span
                      className="alarm-list-dot"
                      style={{ backgroundColor: actionsColorMap[colorIdx] || "#888" }}
                    />
                    <div className="alarm-list-info">
                      <span className="alarm-list-name">
                        {alarm.name || action?.name || alarm.actionTypeId}
                      </span>
                      <span className="alarm-list-detail">
                        {formatDuration(alarm.durationSeconds)}
                        {alarm.isRecurring ? ` ${t("event.alarmModal.recurringLabel")}` : ` ${t("event.alarmModal.onceLabel")}`}
                        {!alarm.eventId && ` ${t("event.alarmModal.globalLabel")}`}
                      </span>
                    </div>
                    <button
                      className="alarm-list-edit"
                      onClick={() => handleEdit(alarm)}
                      title="Edit"
                    >
                      <i className="fas fa-pen"></i>
                    </button>
                    <button
                      className="alarm-list-delete"
                      onClick={() => onDelete(alarm.id)}
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add/Edit form */}
          <div className="alarm-form">
            <h6 className="alarm-form-title">
              {editingId ? t("event.alarmModal.editAlarm") : t("event.alarmModal.addAlarm")}
            </h6>
            <input
              type="text"
              className="form-control alarm-input"
              placeholder={t("event.alarmModal.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <select
              className="form-control alarm-input"
              value={actionTypeId}
              onChange={(e) => setActionTypeId(e.target.value)}
            >
              <option value="">{t("event.alarmModal.selectAction")}</option>
              {event.actions.map((a, i) => (
                <option key={a.id} value={a.typeId}>
                  {a.name} ({t("event.alarmModal.button")} {String(a.typeId).startsWith("custom-") ? "C" + (parseInt(a.typeId.split("-")[1]) + 1) : a.typeId})
                </option>
              ))}
            </select>
            <div className="alarm-duration-row">
              <label>{t("event.alarmModal.duration")}</label>
              <input
                type="number"
                className="form-control alarm-duration-input"
                min={0}
                max={59}
                value={minutes}
                onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
              />
              <span>{t("common.minute")}</span>
              <input
                type="number"
                className="form-control alarm-duration-input"
                min={0}
                max={59}
                value={seconds}
                onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
              />
              <span>{t("common.second")}</span>
            </div>
            <div className="alarm-toggle-row">
              <label className="alarm-toggle">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                />
                <span>{t("event.alarmModal.recurring")}</span>
              </label>
              <label className="alarm-toggle">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                />
                <span>{t("event.alarmModal.globalAllActivities")}</span>
              </label>
            </div>
            <div className="alarm-form-actions">
              <button className="btn btn-sm btn-success" onClick={handleSubmit}>
                {editingId ? t("event.alarmModal.update") : t("event.alarmModal.addAlarm")}
              </button>
              {editingId && (
                <button className="btn btn-sm btn-outline-secondary" onClick={resetForm}>
                  {t("common.cancel")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccordionSection({ title, children, defaultOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="accordion-section">
      <button className="accordion-header" onClick={() => setOpen(!open)}>
        <span className="accordion-title">{title}</span>
        <span className="accordion-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}

function EventPage() {
  const { t } = useTranslation();
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [timeArray, setTimeArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [averages, setAverages] = useState({});
  const [sortedKeys, setSortedKeys] = useState([]);
  const [lastClickedAction, setLastClickedAction] = useState(null);
  const timeArrayRef = useRef([]);
  const actionQueueRef = useRef(Promise.resolve());
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");

  const [timedRunning, setTimedRunning] = useState({});
  const [timerTick, setTimerTick] = useState(0);

  useEffect(() => {
    if (Object.keys(timedRunning).length === 0) return;
    const interval = setInterval(() => setTimerTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [Object.keys(timedRunning).length]); // eslint-disable-line react-hooks/exhaustive-deps

  const actionCounts = useMemo(() => {
    return timeArray.reduce((acc, entry) => {
      acc[entry.actionId] = (acc[entry.actionId] || 0) + 1;
      return acc;
    }, {});
  }, [timeArray]);

  const refetchTimeArrayCb = useCallback(async () => {
    try {
      const arr = await EventsService.getTimeArray(eventId);
      timeArrayRef.current = arr;
      setTimeArray(arr);
    } catch {}
  }, [eventId]);

  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const bleDeviceRef = useRef(null);
  const handleActionClickRef = useRef(null);

  // Chart data state
  const [chartData, setChartData] = useState(null);
  const [freqData, setFreqData] = useState(null);

  // Alarm state
  const [alarms, setAlarms] = useState([]);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [lastPressTimes, setLastPressTimes] = useState({});
  const [alarmCountdowns, setAlarmCountdowns] = useState({});
  const [firedAlarms, setFiredAlarms] = useState(new Set());
  const [flashingAlarms, setFlashingAlarms] = useState(new Set());
  const alarmIntervalRef = useRef(null);
  const lastPressTimesRef = useRef(lastPressTimes);
  const firedAlarmsRef = useRef(firedAlarms);
  const flashingAlarmsRef = useRef(flashingAlarms);
  const alarmSoundIntervalRef = useRef(null);
  const sessionStartRef = useRef(Date.now());

  useEffect(() => { lastPressTimesRef.current = lastPressTimes; }, [lastPressTimes]);
  useEffect(() => { firedAlarmsRef.current = firedAlarms; }, [firedAlarms]);
  useEffect(() => { flashingAlarmsRef.current = flashingAlarms; }, [flashingAlarms]);

  // Continuous alarm sound while any alarm is firing
  useEffect(() => {
    if (flashingAlarms.size > 0) {
      playAlarmSound();
      alarmSoundIntervalRef.current = setInterval(() => {
        playAlarmSound();
      }, 2000);
    } else {
      if (alarmSoundIntervalRef.current) {
        clearInterval(alarmSoundIntervalRef.current);
        alarmSoundIntervalRef.current = null;
      }
    }
    return () => {
      if (alarmSoundIntervalRef.current) clearInterval(alarmSoundIntervalRef.current);
    };
  }, [flashingAlarms.size]); // eslint-disable-line react-hooks/exhaustive-deps

  const isVirtualDevice = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;
      const decoded = jwtDecode(token);
      return decoded.username?.startsWith("VIRT-");
    } catch { return false; }
  }, []);

  const fetchEventDetails = useCallback(async () => {
    try {
      const eventDetails = await EventsService.getEvent(eventId);
      setEvent(eventDetails);
    } catch (error) {
      console.error(error.message);
      alert(t("event.errFetchDetails"));
    }
  }, [eventId]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const newSortedKeys = Array.from(sortedKeys);
    const [removed] = newSortedKeys.splice(result.source.index, 1);
    newSortedKeys.splice(result.destination.index, 0, removed);
    setSortedKeys(newSortedKeys);
  };

  useEffect(() => {
    if (sortedKeys.length === 0) {
      setSortedKeys(Object.keys(averages).sort());
    }
  }, [averages]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateAverages = (timeArray, event) => {
    const actionGroups = {};
    const avgs = {};

    timeArray.forEach((timepoint) => {
      if (!actionGroups[timepoint.actionId]) {
        actionGroups[timepoint.actionId] = [];
      }
      actionGroups[timepoint.actionId].push(
        new Date(timepoint.time).getTime()
      );
    });

    const eventActions = sortActionsByGPIO(event.actions).map((action, index) => ({
      ...action,
      color: actionsColorMap[index % actionsColorMap.length],
    }));

    Object.keys(actionGroups).forEach((actionId) => {
      const times = actionGroups[actionId].sort((a, b) => a - b);
      const intervals = times.slice(1).map((t, i) => t - times[i]);
      const averageInterval = intervals.length
        ? intervals.reduce((a, b) => a + b, 0) / intervals.length
        : 0;
      const minInterval = intervals.length ? Math.min(...intervals) : 0;
      const maxInterval = intervals.length ? Math.max(...intervals) : 0;
      const lastInterval =
        times.length > 1
          ? times[times.length - 1] - times[times.length - 2]
          : 0;

      avgs[actionId] = {
        label: `"${
          event.actions.find((a) => a.id === actionId)?.name || actionId
        }" <-> "${
          event.actions.find((a) => a.id === actionId)?.name || actionId
        }"`,
        value: averageInterval
          ? formatInterval(averageInterval)
          : t("common.NA"),
        minValue: minInterval
          ? formatInterval(minInterval)
          : t("common.NA"),
        maxValue: maxInterval
          ? formatInterval(maxInterval)
          : t("common.NA"),
        lastInterval: lastInterval
          ? formatInterval(lastInterval)
          : t("common.NA"),
        action1Color:
          eventActions.find((a) => a.id === actionId)?.color || "white",
        action2Color:
          eventActions.find((a) => a.id === actionId)?.color || "white",
      };
    });

    for (let i = 0; i < event.actions.length; i++) {
      for (let j = i + 1; j < event.actions.length; j++) {
        const action1 = eventActions[i];
        const action2 = eventActions[j];
        const times1 = actionGroups[action1.id] || [];
        const times2 = actionGroups[action2.id] || [];
        const combined = [
          ...times1.map((t) => ({ time: t, type: 1 })),
          ...times2.map((t) => ({ time: t, type: 2 })),
        ];
        combined.sort((a, b) => a.time - b.time);

        const intervals = [];
        let minInterval = Infinity;
        let maxInterval = -Infinity;
        let lastInterval = 0;

        for (let k = 1; k < combined.length; k++) {
          if (combined[k - 1].type !== combined[k].type) {
            const interval = combined[k].time - combined[k - 1].time;
            intervals.push(interval);
            if (interval < minInterval) minInterval = interval;
            if (interval > maxInterval) maxInterval = interval;
          }
        }

        if (times1.length > 0 && times2.length > 0) {
          const lastTime1 = Math.max(...times1);
          const lastTime2 = Math.max(...times2);
          lastInterval = Math.abs(lastTime1 - lastTime2);
        }

        const averageInterval = intervals.length
          ? intervals.reduce((a, b) => a + b, 0) / intervals.length
          : 0;

        avgs[`${action1.id}-${action2.id}`] = {
          label: `"${action1.name}" <-> "${action2.name}"`,
          value: averageInterval
            ? formatInterval(averageInterval)
            : t("common.NA"),
          minValue:
            minInterval !== Infinity
              ? formatInterval(minInterval)
              : t("common.NA"),
          maxValue:
            maxInterval !== -Infinity
              ? formatInterval(maxInterval)
              : t("common.NA"),
          lastInterval: lastInterval
            ? formatInterval(lastInterval)
            : t("common.NA"),
          action1Color: action1.color || "white",
          action2Color: action2.color || "white",
        };
      }
    }

    setAverages(avgs);
  };

  const fetchTimeArray = useCallback(async () => {
    try {
      const timeArrayData = await EventsService.getTimeArray(eventId);
      timeArrayRef.current = timeArrayData;
      setTimeArray(timeArrayData);
    } catch (error) {
      console.error(error.message);
      alert(t("event.errFetchTimeArray"));
    }
  }, [eventId]);

  // Fetch alarms
  const fetchAlarms = useCallback(async () => {
    try {
      const data = await EventsService.listAlarms(eventId);
      setAlarms(data || []);
    } catch {
      setAlarms([]);
    }
  }, [eventId]);

  const handleEndEventClick = async () => {
    try {
      await EventsService.endEvent(eventId);
      setEvent({ ...event, finished: true });
    } catch (err) {
      setError(t("event.errEndActivity"));
    }
  };

  const handleResumeEventClick = async () => {
    try {
      await EventsService.resumeEvent(eventId);
      setEvent({ ...event, finished: false });
    } catch (err) {
      setError(t("event.errResumeActivity"));
    }
  };

  const handleUndoClick = async () => {
    try {
      await EventsService.undoTimepoint(eventId);
      const newTimeArray = [...timeArrayRef.current];
      newTimeArray.pop();
      timeArrayRef.current = newTimeArray;
      setTimeArray(newTimeArray);
    } catch (err) {
      setError(t("event.errUndoTimepoint"));
    }
  };

  const handleActionClick = async (typeId, time) => {
    if (event.finished) return;

    const action = event.actions.find(
      (a) => String(a.typeId) === String(typeId)
    );

    if (action?.timed) {
      const runningStart = timedRunning[String(typeId)];
      if (!runningStart) {
        setTimedRunning((prev) => ({ ...prev, [String(typeId)]: time }));
        return;
      }
      setTimedRunning((prev) => {
        const next = { ...prev };
        delete next[String(typeId)];
        return next;
      });
      try {
        const timePoint = await EventsService.addTimepoint(
          eventId,
          typeId,
          runningStart,
          time
        );
        if (action) {
          setLastClickedAction(action.id);
          setTimeout(() => setLastClickedAction(null), 600);
        }
        setFlashingAlarms(new Set());
        const now = Date.now();
        setLastPressTimes((prev) => ({ ...prev, [String(typeId)]: now }));
        setFiredAlarms(() => {
          const next = new Set();
          alarms.forEach((a) => next.add(a.id));
          alarms.filter((a) => String(a.actionTypeId) === String(typeId) && a.isRecurring).forEach((a) => next.delete(a.id));
          return next;
        });
        const updated = [...timeArrayRef.current, timePoint];
        timeArrayRef.current = updated;
        setTimeArray(updated);
        calculateAverages(updated, event);
      } catch (err) {
        setError(`Failed to record action: typeId=${typeId}, time=${time}, error=${err.message}`);
        console.error('addTimepoint failed:', { typeId, time, error: err.message });
      }
      return;
    }

    try {
      const timePoint = await EventsService.addTimepoint(
        eventId,
        typeId,
        time
      );
      if (action) {
        setLastClickedAction(action.id);
        setTimeout(() => setLastClickedAction(null), 600);
      }

      setFlashingAlarms(new Set());
      const now = Date.now();
      setLastPressTimes((prev) => ({ ...prev, [String(typeId)]: now }));
      setFiredAlarms(() => {
        const next = new Set();
        alarms.forEach((a) => next.add(a.id));
        alarms.filter((a) => String(a.actionTypeId) === String(typeId) && a.isRecurring).forEach((a) => next.delete(a.id));
        return next;
      });

      const updated = [...timeArrayRef.current, timePoint];
      timeArrayRef.current = updated;
      setTimeArray(updated);
      calculateAverages(updated, event);
    } catch (err) {
      setError(`Failed to record action: typeId=${typeId}, time=${time}, error=${err.message}`);
      console.error('addTimepoint failed:', { typeId, time, error: err.message });
    }
  };

  // Alarm engine
  useEffect(() => {
    if (!event || event.finished || alarms.length === 0) {
      if (alarmIntervalRef.current) clearInterval(alarmIntervalRef.current);
      return;
    }

    alarmIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const newCountdowns = {};
      const lpt = lastPressTimesRef.current;
      const fired = firedAlarmsRef.current;
      const flashing = flashingAlarmsRef.current;
      const sessionStart = sessionStartRef.current;

      alarms.forEach((alarm) => {
        if (!alarm.enabled) return;
        if (fired.has(alarm.id)) return;
        if (flashing.has(alarm.id)) {
          newCountdowns[alarm.id] = 0;
          return;
        }

        const lastPress = lpt[String(alarm.actionTypeId)];
        if (!lastPress) return;

        // Only fire alarms for presses that happened during this session
        if (lastPress < sessionStart) return;

        const elapsed = now - lastPress;
        const remaining = alarm.durationSeconds * 1000 - elapsed;
        newCountdowns[alarm.id] = Math.max(remaining, 0);

        if (remaining <= 0) {
          setFlashingAlarms((prev) => new Set(prev).add(alarm.id));
        }
      });

      setAlarmCountdowns(newCountdowns);
    }, 500);

    return () => {
      if (alarmIntervalRef.current) clearInterval(alarmIntervalRef.current);
    };
  }, [event, alarms]);

  // Initialize lastPressTimes from timeArray
  useEffect(() => {
    if (!event || timeArray.length === 0) return;
    const lpt = {};
    timeArray.forEach((tp) => {
      const action = event.actions.find((a) => a.id === tp.actionId);
      if (action) {
        const t = new Date(tp.time).getTime();
        if (!lpt[String(action.typeId)] || t > lpt[String(action.typeId)]) {
          lpt[String(action.typeId)] = t;
        }
      }
    });
    setLastPressTimes(lpt);
  }, [event, timeArray.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alarm CRUD handlers
  const handleAlarmSave = async (data) => {
    try {
      if (data.id) {
        await EventsService.updateAlarm(data.id, {
          name: data.name,
          actionTypeId: data.actionTypeId,
          durationSeconds: data.durationSeconds,
          isRecurring: data.isRecurring,
        });
      } else {
        await EventsService.createAlarm({
          eventId: data.eventId,
          actionTypeId: data.actionTypeId,
          durationSeconds: data.durationSeconds,
          isRecurring: data.isRecurring,
          name: data.name,
        });
      }
      fetchAlarms();
    } catch {}
  };

  const handleAlarmDelete = async (alarmId) => {
    try {
      await EventsService.deleteAlarm(alarmId);
      fetchAlarms();
    } catch {}
  };

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      await fetchEventDetails();
      await fetchTimeArray();
      await fetchAlarms();
      setLoading(false);
    };
    initializePage();
  }, [fetchEventDetails, fetchTimeArray, fetchAlarms, eventId]);

  // Real-time sync via WebSocket
  useEffect(() => {
    let socket = null;
    let mounted = true;

    const refetchTimeArray = async () => {
      try {
        const arr = await EventsService.getTimeArray(eventId);
        if (!mounted) return;
        timeArrayRef.current = arr;
        setTimeArray(arr);
      } catch {}
    };

    const setupSync = async () => {
      try {
        socket = await getSocket();

        socket.on('event:timepointAdded', (payload) => {
          if (!mounted || payload.eventId !== eventId) return;
          refetchTimeArray();
        });

        socket.on('event:timepointRemoved', (payload) => {
          if (!mounted || payload.eventId !== eventId) return;
          refetchTimeArray();
        });

        socket.on('event:timepointEdited', (payload) => {
          if (!mounted || payload.eventId !== eventId) return;
          refetchTimeArray();
        });

        socket.on('event:ended', (payload) => {
          if (!mounted || payload.eventId !== eventId) return;
          setEvent(prev => prev ? { ...prev, finished: true } : prev);
        });

        socket.on('event:resumed', (payload) => {
          if (!mounted || payload.eventId !== eventId) return;
          setEvent(prev => prev ? { ...prev, finished: false } : prev);
        });
      } catch {}
    };

    setupSync();

    return () => {
      mounted = false;
      if (socket) {
        socket.off('event:timepointAdded');
        socket.off('event:timepointRemoved');
        socket.off('event:timepointEdited');
        socket.off('event:ended');
        socket.off('event:resumed');
      }
    };
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate averages whenever event + timeArray are both loaded
  useEffect(() => {
    if (event && timeArray.length > 0) {
      calculateAverages(timeArray, event);
    }
  }, [event, timeArray.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch chart data
  useEffect(() => {
    if (!event || !eventId) return;
    const fetchChartData = async () => {
      try {
        const [chart, freq] = await Promise.allSettled([
          EventsService.getEventChart(eventId),
          EventsService.getActionFrequency(eventId),
        ]);
        if (chart.status === "fulfilled") setChartData(chart.value);
        if (freq.status === "fulfilled") setFreqData(freq.value);
      } catch {}
    };
    fetchChartData();
  }, [event, eventId, timeArray.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const serviceUuid = "4fafc201-1fb5-459e-8fcc-c5c9c331914b";
  const characteristicUuid = "beb5483e-36e1-4688-b7f5-ea07361b26a8";

  useEffect(() => { handleActionClickRef.current = handleActionClick; });

  const connectToDevice = async (bluetoothDevice) => {
    try {
      bluetoothDevice.addEventListener("gattserverdisconnected", () => {
        setConnected(false);
        exponentialBackoff(
          3,
          2,
          function toTry() {
            return bluetoothDevice.gatt.connect();
          },
          async function success(server) {
            const service = await server.getPrimaryService(serviceUuid);
            let characteristic = await service.getCharacteristic(
              characteristicUuid
            );
            characteristic.addEventListener(
              "characteristicvaluechanged",
              handleNotificationsStable
            );
            await characteristic.startNotifications();
            setConnected(true);
          },
          function fail() {
            console.log("Failed to reconnect.");
          }
        );
      });

      const server = await bluetoothDevice.gatt.connect();
      const service = await server.getPrimaryService(serviceUuid);
      let characteristic = await service.getCharacteristic(characteristicUuid);
      characteristic.addEventListener(
        "characteristicvaluechanged",
        handleNotificationsStable
      );
      await characteristic.startNotifications();
      bleDeviceRef.current = bluetoothDevice;
      localStorage.setItem("ble_was_connected", "true");
      setConnected(true);
    } catch (error) {
      console.error("BLE connect error:", error);
    }
  };

  const handleConnectClick = async () => {
    try {
      let bluetoothDevice = await navigator.bluetooth.requestDevice({
        filters: [{ name: "FishikaBLE" }],
        optionalServices: [serviceUuid],
      });
      await connectToDevice(bluetoothDevice);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Auto-reconnect on page load if previously connected
  useEffect(() => {
    if (isVirtualDevice) return;
    const wasConnected = localStorage.getItem("ble_was_connected");
    if (!wasConnected) return;
    if (!navigator.bluetooth || !navigator.bluetooth.getDevices) return;

    const tryAutoReconnect = async () => {
      try {
        const devices = await navigator.bluetooth.getDevices();
        const device = devices.find((d) => d.name === "FishikaBLE");
        if (device) {
          device.addEventListener("advertisementreceived", async () => {
            await connectToDevice(device);
          });
          await device.watchAdvertisements({ signal: AbortSignal.timeout(5000) });
        }
      } catch (e) {
        console.log("Auto-reconnect not available:", e.message);
      }
    };
    tryAutoReconnect();
  }, [isVirtualDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  const extraBleTypeIds = [24, 25, 26, 27];

  const handleNotificationsStable = (evt) => {
    const value = new TextDecoder().decode(evt.target.value);
    const valueObj = JSON.parse(value);
    let typeId = valueObj.type;
    const time = Math.floor(Date.now() / 1000);

    const extraIdx = extraBleTypeIds.indexOf(Number(typeId));
    if (extraIdx !== -1 && event) {
      const customs = event.actions.filter((a) => String(a.typeId).startsWith("custom-"));
      if (customs[extraIdx]) {
        typeId = customs[extraIdx].typeId;
      } else {
        console.log(`BLE button typeId=${typeId} has no mapped custom action (only ${customs.length} custom actions)`);
        return;
      }
    }

    actionQueueRef.current = actionQueueRef.current.then(() => {
      if (handleActionClickRef.current) {
        return handleActionClickRef.current(typeId, time);
      }
    }).catch(() => {});
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center" style={{ paddingTop: 100 }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <p style={{ color: "red", padding: 32 }}>{error}</p>;
  }

  if (!event) {
    return <p style={{ padding: 32 }}>{t("event.noActivityFound")}</p>;
  }

  const eventActions = sortActionsByGPIO(event.actions).map((action, index) => ({
    ...action,
    color: actionsColorMap[index % actionsColorMap.length],
  }));

  const deviceActions = eventActions.filter(
    (a) => !String(a.typeId).startsWith("custom-")
  );
  const customActions = eventActions.filter(
    (a) => String(a.typeId).startsWith("custom-")
  );

  // Find active alarm countdown per typeId
  const dismissAlarmOverlay = () => {
    const firingAlarms = alarms.filter((a) => flashingAlarms.has(a.id));
    setFlashingAlarms(new Set());
    // Mark all as fired so they don't re-trigger until the action is pressed
    setFiredAlarms((prev) => {
      const next = new Set(prev);
      firingAlarms.forEach((a) => next.add(a.id));
      return next;
    });
  };

  const getAlarmBadge = (typeId) => {
    const matching = alarms.filter(
      (a) => String(a.actionTypeId) === String(typeId) && a.enabled
    );
    if (matching.length === 0) return null;
    const alarm = matching[0];
    const remaining = alarmCountdowns[alarm.id];
    const isFlashing = flashingAlarms.has(alarm.id);
    if (remaining === undefined && !isFlashing) return null;
    return { remaining, isFlashing, alarm };
  };

  return (
    <>
      {/* Alarm Overlay */}
      {flashingAlarms.size > 0 && (
        <div className="alarm-fullscreen-overlay" onClick={dismissAlarmOverlay}>
          <div className="alarm-fullscreen-content">
            <span className="alarm-fullscreen-icon">🔔</span>
            <h1 className="alarm-fullscreen-title">{t("event.alarm")}</h1>
            {alarms
              .filter((a) => flashingAlarms.has(a.id))
              .map((a) => {
                const action = event.actions.find(
                  (ac) => String(ac.typeId) === String(a.actionTypeId)
                );
                return (
                  <p key={a.id} className="alarm-fullscreen-action">
                    {a.name || action?.name || a.actionTypeId}
                  </p>
                );
              })}
            <p className="alarm-fullscreen-hint">{t("event.clickToDismiss")}</p>
          </div>
        </div>
      )}

      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              id="sidebar-toggle"
              onClick={(e) => {
                e.preventDefault();
                window.__toggleSidebar && window.__toggleSidebar(e);
              }}
            >
              <i className="fas fa-bars"></i>
            </a>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        <div className="event-heading-row">
          {editingName ? (
            <div className="event-name-edit">
              <input
                type="text"
                className="event-name-input"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (nameInput.trim() && nameInput.trim() !== event.name) {
                      EventsService.renameEvent(eventId, nameInput.trim()).then(() => {
                        setEvent(prev => ({ ...prev, name: nameInput.trim() }));
                      });
                    }
                    setEditingName(false);
                  } else if (e.key === "Escape") {
                    setEditingName(false);
                  }
                }}
                autoFocus
              />
              <button
                className="event-name-save"
                onClick={() => {
                  if (nameInput.trim() && nameInput.trim() !== event.name) {
                    EventsService.renameEvent(eventId, nameInput.trim()).then(() => {
                      setEvent(prev => ({ ...prev, name: nameInput.trim() }));
                    });
                  }
                  setEditingName(false);
                }}
              >
                <i className="fas fa-check"></i>
              </button>
              <button
                className="event-name-cancel"
                onClick={() => setEditingName(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ) : (
            <h1
              className="event-heading event-heading-editable"
              onClick={() => { setNameInput(event.name); setEditingName(true); }}
              title={t("event.clickToEditName")}
            >
              {event.name}
              <i className="fas fa-pen event-heading-edit-icon"></i>
            </h1>
          )}
        </div>
        {/* Toolbar */}
        <div className="event-toolbar glass-card">
          {!isVirtualDevice && (
            <button
              className={`event-toolbar-btn ${
                connected ? "event-toolbar-btn-connected" : ""
              }`}
              onClick={!connected ? handleConnectClick : undefined}
              title={connected ? t("event.bluetoothConnected") : t("event.connectBluetooth")}
            >
              <i className="fab fa-bluetooth-b"></i>
              {connected && <span className="ble-pulse-dot"></span>}
              <span className="event-toolbar-label">{t("event.ble")}</span>
            </button>
          )}
          <button
            className={`event-toolbar-btn ${alarms.length > 0 ? "event-toolbar-btn-alarm-active" : ""}`}
            onClick={() => setShowAlarmModal(true)}
            title={t("event.alarms")}
          >
            <i className="fas fa-bell"></i>
            {alarms.length > 0 && (
              <span className="alarm-badge-count">{alarms.length}</span>
            )}
            <span className="event-toolbar-label">{t("event.alarms")}</span>
          </button>
          <span className="event-toolbar-live-indicator" title={t("common.live")}>
            <span className="live-dot"></span>
            <span className="event-toolbar-label">{t("common.live")}</span>
          </span>
          {!event.finished ? (
            <button
              className="event-toolbar-btn event-toolbar-btn-danger"
              onClick={handleEndEventClick}
              title={t("event.endActivity")}
            >
              <i className="fas fa-stop"></i>
              <span className="event-toolbar-label">{t("event.end")}</span>
            </button>
          ) : (
            <button
              className="event-toolbar-btn event-toolbar-btn-resume"
              onClick={handleResumeEventClick}
              title={t("event.resumeActivity")}
            >
              <i className="fas fa-play"></i>
              <span className="event-toolbar-label">{t("event.resume")}</span>
            </button>
          )}
          <button
            className="event-toolbar-btn event-toolbar-btn-warning"
            onClick={handleUndoClick}
            title={t("event.undoLast")}
          >
            <i className="fas fa-undo"></i>
            <span className="event-toolbar-label">{t("event.undo")}</span>
          </button>
          <button
            className="event-toolbar-btn"
            onClick={() => navigate(`/event/${eventId}/step2`)}
            title={t("event.editActivity")}
          >
            <i className="fas fa-cog"></i>
            <span className="event-toolbar-label">{t("common.edit")}</span>
          </button>
        </div>

        {/* Device Action Buttons */}
        <div className="event-actions-grid">
          {deviceActions.map((action) => {
            const badge = getAlarmBadge(action.typeId);
            const timedStart = timedRunning[String(action.typeId)];
            return (
              <button
                key={action.id}
                className={`event-action-btn ${
                  lastClickedAction === action.id ? "event-action-btn-pulse" : ""
                } ${badge?.isFlashing ? "event-action-btn-alarm" : ""} ${
                  timedStart ? "event-action-btn-timed-running" : ""
                }`}
                onClick={() =>
                  handleActionClick(
                    action.typeId,
                    Math.floor(Date.now() / 1000)
                  )
                }
                style={{ "--action-color": action.color }}
              >
                <span className="event-action-count">
                  {actionCounts[action.id] || 0}
                </span>
                <span className="event-action-name">{action.name}</span>
                {timedStart && (
                  <span className="event-action-timed-badge">
                    <i className="fas fa-stopwatch" style={{ marginRight: 4, fontSize: 10 }}></i>
                    {formatElapsed(timedStart)}
                  </span>
                )}
                {badge && badge.remaining !== undefined && (
                  <span
                    className={`event-action-alarm-badge ${
                      badge.isFlashing ? "alarm-flashing" : ""
                    } ${
                      badge.remaining > 0 && badge.remaining < 10000
                        ? "alarm-warning"
                        : ""
                    }`}
                  >
                    <i className="fas fa-bell" style={{ marginRight: 4, fontSize: 10 }}></i>
                    {badge.remaining > 0
                      ? formatCountdown(badge.remaining)
                      : t("event.alarm")}
                  </span>
                )}
                <span className="event-action-ripple"></span>
              </button>
            );
          })}
        </div>

        {/* Device Analytics */}
        {Object.keys(averages).length > 0 && (() => {
          const deviceActionIds = new Set(deviceActions.map(a => a.id));
          const deviceKeys = sortedKeys.filter(key => {
            if (deviceActionIds.has(key)) return true;
            const parts = key.split("-");
            return parts.length === 2 && deviceActionIds.has(parts[0]) && deviceActionIds.has(parts[1]);
          });
          if (deviceKeys.length === 0) return null;
          return (
            <AccordionSection title={t("event.deviceAnalytics", { count: deviceKeys.length })}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="averages-list" direction="horizontal">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="event-analytics-grid"
                    >
                      {deviceKeys.map((key, index) => {
                        const average = averages[key];
                        if (!average) return null;
                        return (
                          <Draggable key={key} draggableId={key} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="glass-card"
                                style={provided.draggableProps.style}
                              >
                                <div className="glass-card-colors">
                                  <span className="glass-card-dot" style={{ backgroundColor: average.action1Color }}></span>
                                  {average.action1Color !== average.action2Color && (
                                    <span className="glass-card-dot" style={{ backgroundColor: average.action2Color }}></span>
                                  )}
                                </div>
                                <p className="glass-card-label">{average.label}</p>
                                <p className="glass-card-value">{average.value}</p>
                                <div className="glass-card-stats">
                                  {average.minValue !== t("common.NA") && <span>{t("common.min")} <strong>{average.minValue}</strong></span>}
                                  {average.maxValue !== t("common.NA") && <span>{t("common.max")} <strong>{average.maxValue}</strong></span>}
                                  {average.lastInterval !== t("common.NA") && <span>{t("common.last")} <strong>{average.lastInterval}</strong></span>}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </AccordionSection>
          );
        })()}

        {/* Device Timeline */}
        <AccordionSection title={t("event.deviceTimeline")}>
          <TimelineSelector
            timeArray={timeArray.filter(tp => deviceActions.some(a => a.id === tp.actionId))}
            actions={deviceActions}
          />
        </AccordionSection>

        {/* Device Timepoints */}
        <AccordionSection title={t("event.deviceTimepoints", { count: timeArray.filter(tp => deviceActions.some(a => a.id === tp.actionId)).length })}>
          <TimepointCalendar
            timeArray={timeArray.filter(tp => deviceActions.some(a => a.id === tp.actionId))}
            actions={deviceActions}
            onTimepointChanged={refetchTimeArrayCb}
          />
        </AccordionSection>

        {/* ====== CUSTOM ACTIONS SECTION ====== */}
        {customActions.length > 0 && (
          <>
            <div className="event-section-divider">
              <span className="event-section-divider-line"></span>
              <span className="event-section-divider-text">{t("event.customActions")}</span>
              <span className="event-section-divider-line"></span>
            </div>

            <div className="event-actions-grid">
              {customActions.map((action) => {
                const badge = getAlarmBadge(action.typeId);
                const timedStart = timedRunning[String(action.typeId)];
                return (
                  <button
                    key={action.id}
                    className={`event-action-btn ${
                      lastClickedAction === action.id
                        ? "event-action-btn-pulse"
                        : ""
                    } ${badge?.isFlashing ? "event-action-btn-alarm" : ""} ${
                      timedStart ? "event-action-btn-timed-running" : ""
                    }`}
                    onClick={() =>
                      handleActionClick(
                        action.typeId,
                        Math.floor(Date.now() / 1000)
                      )
                    }
                    style={{ "--action-color": action.color }}
                  >
                    <span className="event-action-count">
                      {actionCounts[action.id] || 0}
                    </span>
                    <span className="event-action-name">{action.name}</span>
                    {timedStart && (
                      <span className="event-action-timed-badge">
                        <i className="fas fa-stopwatch" style={{ marginRight: 4, fontSize: 10 }}></i>
                        {formatElapsed(timedStart)}
                      </span>
                    )}
                    {badge && badge.remaining !== undefined && (
                      <span
                        className={`event-action-alarm-badge ${
                          badge.isFlashing ? "alarm-flashing" : ""
                        } ${
                          badge.remaining > 0 && badge.remaining < 10000
                            ? "alarm-warning"
                            : ""
                        }`}
                      >
                        <i className="fas fa-bell" style={{ marginRight: 4, fontSize: 10 }}></i>
                        {badge.remaining > 0
                          ? formatCountdown(badge.remaining)
                          : t("event.alarm")}
                      </span>
                    )}
                    <span className="event-action-ripple"></span>
                  </button>
                );
              })}
            </div>

            {/* Custom Analytics */}
            {Object.keys(averages).length > 0 && (() => {
              const customActionIds = new Set(customActions.map(a => a.id));
              const customKeys = sortedKeys.filter(key => {
                if (customActionIds.has(key)) return true;
                const parts = key.split("-");
                return parts.length === 2 && customActionIds.has(parts[0]) && customActionIds.has(parts[1]);
              });
              if (customKeys.length === 0) return null;
              return (
                <AccordionSection title={t("event.customAnalytics", { count: customKeys.length })}>
                  <div className="event-analytics-grid">
                    {customKeys.map((key) => {
                      const average = averages[key];
                      if (!average) return null;
                      return (
                        <div key={key} className="glass-card">
                          <div className="glass-card-colors">
                            <span className="glass-card-dot" style={{ backgroundColor: average.action1Color }}></span>
                            {average.action1Color !== average.action2Color && (
                              <span className="glass-card-dot" style={{ backgroundColor: average.action2Color }}></span>
                            )}
                          </div>
                          <p className="glass-card-label">{average.label}</p>
                          <p className="glass-card-value">{average.value}</p>
                          <div className="glass-card-stats">
                            {average.minValue !== t("common.NA") && <span>{t("common.min")} <strong>{average.minValue}</strong></span>}
                            {average.maxValue !== t("common.NA") && <span>{t("common.max")} <strong>{average.maxValue}</strong></span>}
                            {average.lastInterval !== t("common.NA") && <span>{t("common.last")} <strong>{average.lastInterval}</strong></span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionSection>
              );
            })()}

            {/* Custom Timeline */}
            <AccordionSection title={t("event.customTimeline")}>
              <TimelineSelector
                timeArray={timeArray.filter(tp => customActions.some(a => a.id === tp.actionId))}
                actions={customActions}
              />
            </AccordionSection>

            {/* Custom Timepoints */}
            <AccordionSection title={t("event.customTimepoints", { count: timeArray.filter(tp => customActions.some(a => a.id === tp.actionId)).length })}>
              <TimepointCalendar
                timeArray={timeArray.filter(tp => customActions.some(a => a.id === tp.actionId))}
                actions={customActions}
                onTimepointChanged={refetchTimeArrayCb}
              />
            </AccordionSection>
          </>
        )}

        {/* ====== SESSION SUMMARY (when finished) ====== */}
        {event.finished && timeArray.length > 0 && (() => {
          const times = timeArray.map((tp) => new Date(tp.time).getTime()).sort((a, b) => a - b);
          const duration = times.length >= 2 ? times[times.length - 1] - times[0] : 0;
          const totalPerAction = {};
          let fastestInterval = Infinity;
          let longestGap = 0;
          const actionTimestamps = {};

          timeArray.forEach((tp) => {
            totalPerAction[tp.actionId] = (totalPerAction[tp.actionId] || 0) + 1;
            if (!actionTimestamps[tp.actionId]) actionTimestamps[tp.actionId] = [];
            actionTimestamps[tp.actionId].push(new Date(tp.time).getTime());
          });

          Object.values(actionTimestamps).forEach((stamps) => {
            stamps.sort((a, b) => a - b);
            for (let i = 1; i < stamps.length; i++) {
              const gap = stamps[i] - stamps[i - 1];
              if (gap < fastestInterval) fastestInterval = gap;
              if (gap > longestGap) longestGap = gap;
            }
          });

          const mostPressedId = Object.entries(totalPerAction).sort((a, b) => b[1] - a[1])[0]?.[0];
          const mostPressedAction = event.actions.find((a) => a.id === mostPressedId);

          return (
            <div className="glass-card session-summary-card">
              <h6 className="event-section-title" style={{ marginBottom: 16 }}>
                <i className="fas fa-flag-checkered" style={{ marginRight: 8 }}></i>
                {t("event.sessionComplete")}
              </h6>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div className="session-summary-stat">
                  <span className="session-summary-label">{t("event.sessionDuration")}</span>
                  <span className="session-summary-value">{formatInterval(duration)}</span>
                </div>
                <div className="session-summary-stat">
                  <span className="session-summary-label">{t("event.totalActions")}</span>
                  <span className="session-summary-value" style={{ color: "#0A84FF" }}>{timeArray.length}</span>
                </div>
                {fastestInterval !== Infinity && (
                  <div className="session-summary-stat">
                    <span className="session-summary-label">{t("event.fastestInterval")}</span>
                    <span className="session-summary-value" style={{ color: "#30D158" }}>{formatInterval(fastestInterval)}</span>
                  </div>
                )}
                {longestGap > 0 && (
                  <div className="session-summary-stat">
                    <span className="session-summary-label">{t("event.longestGap")}</span>
                    <span className="session-summary-value" style={{ color: "#FF453A" }}>{formatInterval(longestGap)}</span>
                  </div>
                )}
                {mostPressedAction && (
                  <div className="session-summary-stat">
                    <span className="session-summary-label">{t("event.mostPressed")}</span>
                    <span className="session-summary-value">{mostPressedAction.name} ({totalPerAction[mostPressedId]})</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ====== VISUAL CHARTS ====== */}
        {(chartData || freqData || timeArray.length > 0) && (
          <div className="event-charts-section">
            <h6 className="event-section-title">{t("event.visualAnalytics")}</h6>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {/* Action Counts Bar Chart (from local data) */}
              {eventActions.length > 0 && timeArray.length > 0 && (
                <div className="glass-card" style={{ flex: "1 1 350px", padding: 20 }}>
                  <h6 style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>
                    {t("event.actionCounts")}
                  </h6>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={eventActions.map((a, i) => ({
                        name: a.name,
                        count: actionCounts[a.id] || 0,
                        color: a.color,
                      }))}
                      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="name"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                      />
                      <YAxis
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15,15,25,0.95)",
                          border: "0.5px solid rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          color: "#fff",
                        }}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {eventActions.map((a, i) => (
                          <Cell key={i} fill={a.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Timeline Scatter */}
              {Array.isArray(chartData) && chartData.length > 0 && (
                <div className="glass-card" style={{ flex: "1 1 400px", padding: 20 }}>
                  <h6 style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>
                    {t("event.actionTimeline")}
                  </h6>
                  <ResponsiveContainer width="100%" height={280}>
                    <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="x"
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={(v) => format(new Date(v), "HH:mm")}
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                      />
                      <YAxis
                        dataKey="y"
                        type="number"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                        tickFormatter={(v) => event.actions?.[v]?.name || `#${v}`}
                      />
                      <Tooltip
                        content={({ payload }) => {
                          if (!payload?.[0]) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="chart-tooltip">
                              <div>{d.actionName}</div>
                              <div style={{ fontSize: 11, opacity: 0.6 }}>{format(new Date(d.x), "HH:mm:ss")}</div>
                            </div>
                          );
                        }}
                      />
                      <Scatter
                        data={chartData.map((p) => {
                          const actionIndex = event.actions.findIndex(
                            (a) => a.id === p.actionId || String(a.typeId) === String(p.typeId)
                          );
                          return {
                            x: new Date(p.time).getTime(),
                            y: actionIndex >= 0 ? actionIndex : 0,
                            actionName: p.actionName,
                            color: actionsColorMap[(actionIndex >= 0 ? actionIndex : 0) % actionsColorMap.length],
                          };
                        })}
                        shape={(props) => (
                          <circle cx={props.cx} cy={props.cy} r={4} fill={props.payload.color} fillOpacity={0.8} />
                        )}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Hourly Activity (computed from local timeArray using local timezone) */}
              {timeArray.length > 0 && (() => {
                const localPerHour = Array(24).fill(0);
                timeArray.forEach((tp) => { localPerHour[new Date(tp.time).getHours()]++; });
                if (!localPerHour.some((v) => v > 0)) return null;
                return (
                  <div className="glass-card" style={{ flex: "1 1 400px", padding: 20 }}>
                    <h6 style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>
                      {t("event.hourlyActivity")}
                    </h6>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={localPerHour.map((count, hour) => ({
                          hour: `${hour}:00`,
                          count,
                        }))}
                        margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis
                          dataKey="hour"
                          stroke="rgba(255,255,255,0.3)"
                          tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 9 }}
                          interval={2}
                        />
                        <YAxis
                          stroke="rgba(255,255,255,0.3)"
                          tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(15,15,25,0.95)",
                            border: "0.5px solid rgba(255,255,255,0.12)",
                            borderRadius: 10,
                            color: "#fff",
                          }}
                        />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#0A84FF" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Per-Action Frequency (from API) */}
              {freqData?.perAction && freqData.perAction.length > 0 && (
                <div className="glass-card" style={{ flex: "1 1 350px", padding: 20 }}>
                  <h6 style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>
                    {t("event.actionFrequency")}
                  </h6>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={freqData.perAction.map((a, i) => {
                        const action = event.actions.find((ac) => String(ac.typeId) === String(a.typeId));
                        return {
                          name: action?.name || a.typeId,
                          count: a.count || 0,
                        };
                      })}
                      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="name"
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                      />
                      <YAxis
                        stroke="rgba(255,255,255,0.3)"
                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15,15,25,0.95)",
                          border: "0.5px solid rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          color: "#fff",
                        }}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {freqData.perAction.map((a, i) => {
                          const actionIdx = event.actions.findIndex((ac) => String(ac.typeId) === String(a.typeId));
                          return <Cell key={i} fill={actionsColorMap[(actionIdx >= 0 ? actionIdx : i) % actionsColorMap.length]} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <AlarmModal
        show={showAlarmModal}
        onClose={() => setShowAlarmModal(false)}
        event={event}
        alarms={alarms}
        onSave={handleAlarmSave}
        onDelete={handleAlarmDelete}
      />
    </>
  );
}

export default EventPage;
